import decodeJwt from 'jwt-decode';
import { Nullable } from '../types';
import { CookieStorageService } from './storage/cookie-storage';
import { Storage } from './storage/storage.interface';

const LOGGED_KEY = 'logged';
const TOKEN_KEY = 'token';
const REFRESH_TOKEN_KEY = 'refresh_token';
const ROLES_KEY = 'roles';
const DATA_KEY = 'user_data';

export class UserAuth {
    private storage: Storage;

    public constructor(storage: Storage) {
        this.storage = storage;
    }

    public login(token: string, refreshToken: string, remember = false): void {
        this.setLoggedIn(true);
        this.setToken(token, remember ? 365 : 1);
        this.setRefreshToken(refreshToken, remember ? 365 : 1);
        const decoded: { username: string; roles: string[] } = decodeJwt(token);

        this.setData({ username: decoded.username, roles: decoded.roles });
        decoded.roles && this.setRoles(decoded.roles);
    }

    public logout(): Promise<void> {
        this.setLoggedIn(false);

        this.storage.removeItem(TOKEN_KEY);
        this.storage.removeItem(ROLES_KEY);
        this.storage.removeItem(DATA_KEY);
        this.storage.removeItem(REFRESH_TOKEN_KEY);

        return Promise.resolve();
    }

    public getToken(): Nullable<string> {
        return this.storage.getItem(TOKEN_KEY);
    }

    public getData(): Nullable<AnyObject> {
        const stringData = this.storage.getItem(DATA_KEY);
        return stringData ? JSON.parse(stringData) : stringData;
    }

    public getRoles(): Array<string> {
        const roles = this.storage.getItem(ROLES_KEY);

        return roles ? roles.split(',') : [];
    }

    public isLoggedIn(): boolean {
        const token = this.storage.getItem(TOKEN_KEY);
        const logged = this.storage.getItem(LOGGED_KEY);
        if (!token || !logged) return false;

        try {
            const decoded: any = decodeJwt(token);
            if (decoded && decoded.exp && decoded.exp * 1000 < Date.now()) {
                const refreshToken = this.getRefreshToken();
                if (!refreshToken) {
                    return false;
                }
                try {
                    const decodedRefresh: any = decodeJwt(refreshToken);
                    if (decodedRefresh && decodedRefresh.exp && decodedRefresh.exp * 1000 < Date.now()) {
                        return false;
                    }
                } catch {
                    return false;
                }
            }
        } catch {
            return false;
        }

        return true;
    }

    private setData(decodedData: AnyObject) {
        this.storage.setItem(DATA_KEY, JSON.stringify(decodedData));
    }

    private setToken(token: string, expire = 365) {
        this.storage.setItem(TOKEN_KEY, token, expire);
    }

    private setRefreshToken(token: string, expire = 365) {
        this.storage.setItem(REFRESH_TOKEN_KEY, token, expire);
    }

    private setRoles(roles: Array<string>) {
        this.storage.setItem(ROLES_KEY, roles.join(','));
    }

    private setLoggedIn(value: boolean) {
        if (value) {
            this.storage.setItem(LOGGED_KEY, (+new Date()).toString());
        } else {
            this.storage.removeItem(LOGGED_KEY);
        }
    }

    getRefreshToken(): Nullable<string> {
        return this.storage.getItem(REFRESH_TOKEN_KEY);
    }
}

// const UserAuthService = new UserAuth(localStorage);
const UserAuthService = new UserAuth(CookieStorageService);

export default UserAuthService;
